import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { add, format, isSameDay, parseISO } from 'date-fns';
import { isEmpty, omit, debounce } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ChangeEvent,
} from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Stack from 'react-bootstrap/Stack';
import { useForm } from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import {
  BookingRequestsValidateService,
  ProviderBookingValidate,
  ProviderLimitedDetail,
} from '../../../../api';
import { DATE_FORMAT } from '../../../constants/time';
import { FormSubmitContext } from '../../../contexts/FormSubmitContext';
import { useCurrencyFormatter } from '../../../hooks/use-currency-formatter';
import { AddShiftModal } from '../AddShiftModal';
import {
  getAvailablePresets,
  getInitialShifts,
  updateShiftByPreset,
} from '../Helper/ShiftSelectorHelper';
import { formatProviderAvailabilityLabel } from '../ProviderShiftSelectorModal/ProviderShiftSelectorModal.utils';
import { ProviderScheduleWithKey } from '../Scheduler.types';
import {
  FormOutput,
  IndividualShiftPresets,
  IShiftPreset,
  Shift,
  SHIFT_BOOTSTRAP_COL_NAME,
  ShiftPreset,
  SHIFTS_PRESET_MAP,
} from '../ShiftPreset';

interface Props {
  isBooking: boolean;
  facilityId: string | null;
  providerId: string;
  provider?: ProviderLimitedDetail;
  connectedFacility?: any;
  isLoading: boolean;
  selectedDates: Date[];
  schedules?: ProviderScheduleWithKey[];
  onConfirm: (shiftByDate: FormOutput) => void;
  setSelectedDates: Dispatch<SetStateAction<Date[]>>;
  onOpenConfirmPopup: () => void;
}

export const HCOShiftSelectorModal2 = ({
  isBooking,
  isLoading,
  facilityId,
  providerId,
  provider,
  connectedFacility,
  selectedDates,
  schedules = [],
  onConfirm,
  onOpenConfirmPopup,
  setSelectedDates,
}: Props) => {
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const { formatCurrency } = useCurrencyFormatter({ placeholder: '-' });

  const [selectedDatesWithShifts, setSelectedDatesWithShifts] = useState<Date[]>(selectedDates);
  const [presets, setPresets] = useState<IShiftPreset[]>([]);
  const [openAddShiftModal, setOpenAddShiftModal] = useState<boolean>(false);
  const [customSelectedTimes, setCustomSelectedTimes] = useState<Record<string, Shift>>({});
  const [shifts, setShifts] = useState<Shift[]>(() => getInitialShifts(selectedDates, schedules));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidateLoading, setIsValidateLoading] = useState<boolean>(false);
  const { handleSubmit, formState, getValues, register } = useForm<FormOutput>({
    defaultValues: { shifts },
  });
  const shortDates = useMemo(
    () => new Set(selectedDatesWithShifts.map((date) => format(date, DATE_FORMAT))),
    [selectedDatesWithShifts]
  );
  const schedulesForAdding = useMemo(() => {
    const today = new Date();
    return schedules.filter(
      ({ start_on, is_available }) =>
        is_available && !shortDates.has(start_on) && new Date(start_on) >= today
    );
  }, [schedules, shortDates]);
  const formErrors = formState.errors;

  const { mutateAsync: bookingValidate } = useMutation({
    mutationFn: async (data: { shifts: Shift[] }) => {
      if (isValidateLoading) return;
      setIsValidateLoading(true);
      setErrors({});

      const healthcare_facility_id = facilityId as string;
      const provider_id = providerId;
      const schedules = data.shifts.map((shift) => {
        const date = parseISO(shift.startOn);
        const nextDay = add(date, { days: 1 });

        const end_date =
          shift.startTime >= shift.endTime
            ? format(nextDay, DATE_FORMAT)
            : format(date, DATE_FORMAT);

        return {
          provider_rate_type: shift.presetType,
          end_on: end_date,
          start_on: shift.startOn,
          start_time: shift.startTime,
          end_time: shift.endTime,
        };
      });

      const { comment } = getValues();

      return BookingRequestsValidateService.bookingRequestsValidatePartialUpdate({
        requestBody: {
          schedules,
          healthcare_facility_id,
          provider_id,
          comment,
        },
      });
    },
    onError: (errors) => {
      console.error(errors);
      setIsValidateLoading(false);
    },
    onSuccess: (validatedShifts) => {
      const response = validatedShifts as any as ProviderBookingValidate[];
      setPresets(getAvailablePresets(connectedFacility, SHIFTS_PRESET_MAP, response));

      const updatedFields = shifts.map((field) => {
        const validatedShift = response.find((item) => item.id === field.id);

        if (!validatedShift?.valid) {
          setErrors((prev) => ({
            ...prev,
            [field.key]: validatedShift?.error as string,
          }));

          return {
            ...field,
            totalRatesValue: validatedShift?.total_value || '0',
          };
        }

        if (validatedShift) {
          return {
            ...field,
            startOn: validatedShift.start_on,
            endOn: validatedShift.end_on,
            presetType: validatedShift.provider_rate_type,
            providerRate: validatedShift.provider_rate,
            totalRatesValue: validatedShift.total_value,
          };
        }
        return field;
      });
      updatedFields.sort((a, b) => new Date(a.startOn).getTime() - new Date(b.startOn).getTime());

      setShifts(updatedFields);

      setIsValidateLoading(false);
      return updatedFields;
    },
  });

  const addNewShift = () => {
    setOpenAddShiftModal(true);
  };

  const colSize = useMemo(() => SHIFT_BOOTSTRAP_COL_NAME(presets.length), [presets]);
  const totalPayment = useMemo(() => {
    return shifts.reduce((acc, shiftField) => {
      return acc + (shiftField?.totalRatesValue ? +shiftField.totalRatesValue : 0);
    }, 0);
  }, [shifts]);

  const onSubmit = async ({ comment }: FormOutput) => {
    const response = await bookingValidate({ shifts });
    const validateResponse = response as any as ProviderBookingValidate[];
    const isInValid = validateResponse?.some((item) => !item.valid);

    if (!isEmpty(formState.errors) || isInValid) return;

    onConfirm({ shifts, comment });
    setSelectedDatesWithShifts([]);
  };

  const applyPreset = async (preset: IShiftPreset): Promise<void> => {
    const updatedShifts = shifts.map((shift) => updateShiftByPreset(preset, shift));
    setShifts(updatedShifts);
    await bookingValidate({ shifts: updatedShifts });
    setCustomSelectedTimes({});
  };

  const onAddShift = async (checkedDates: Date[]): Promise<void> => {
    setSelectedDatesWithShifts((prevState) => [...prevState, ...checkedDates]);
    const newShifts = getInitialShifts(checkedDates, schedules).map((shift) => ({
      ...shift,
      isNew: true,
    }));

    const updatedShifts = [...newShifts, ...shifts].sort(
      (a, b) => new Date(a.startOn).getTime() - new Date(b.startOn).getTime()
    );

    setShifts(updatedShifts.map((el, idx) => ({ ...el, id: idx + 1 })));
    await bookingValidate({ shifts: updatedShifts });
  };

  const debouncedValidate = useCallback(
    debounce(async (shiftsToValidate: Shift[]) => {
      if (isValidateLoading) return;
      setIsValidateLoading(true);
      try {
        await bookingValidate({ shifts: shiftsToValidate });
      } finally {
        setIsValidateLoading(false);
      }
    }, 500),
    []
  );

  const handleCustomTimeChange =
    (shift: Shift, field: 'startTime' | 'endTime') =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const newValue = e.target.value;

      setCustomSelectedTimes((prev) => ({
        ...prev,
        [shift.key]: {
          ...shift,
          ...prev[shift.key],
          [field]: newValue,
        },
      }));

      const startTime = field === 'startTime' ? newValue : shift.startTime;
      const endTime = field === 'endTime' ? newValue : shift.endTime;
      const scheduleDate = parseISO(shift.startOn);
      const nextDay = add(scheduleDate, { days: 1 });
      const endOn =
        startTime >= endTime ? format(nextDay, DATE_FORMAT) : format(scheduleDate, DATE_FORMAT);

      const updatedShifts = shifts.map((el) =>
        shift.key !== el.key ? el : { ...el, startTime, endTime, endOn, isNew: false }
      );

      setShifts(updatedShifts);
      debouncedValidate(updatedShifts);
    };

  const handleRemoveShift = async (shiftToDelete: Shift) => {
    setSelectedDatesWithShifts((prevDates) =>
      prevDates.filter((date) => format(date, DATE_FORMAT) !== shiftToDelete.startOn)
    );

    const newShifts = shifts
      .filter((el) => el.key !== shiftToDelete.key)
      .map((shift, idx) => ({ ...shift, id: idx + 1 }));

    setShifts(newShifts);
    await bookingValidate({ shifts: newShifts });
  };

  const applyIndividualPreset = useCallback(
    async (preset: IShiftPreset, shiftField: Shift): Promise<void> => {
      setShifts((prevState) =>
        prevState.map((shift) =>
          shift.id !== shiftField.id ? shift : updateShiftByPreset(preset, shift)
        )
      );

      const updatedShifts = shifts.map((shift) =>
        shift.id !== shiftField.id ? shift : updateShiftByPreset(preset, shift)
      );

      setShifts(updatedShifts);
      await bookingValidate({ shifts: updatedShifts });

      setCustomSelectedTimes({});
      setErrors({});
    },
    [bookingValidate, shifts]
  );

  const handleApplyToAll = async (key: string): Promise<void> => {
    const { startTime, endTime, presetType, startOn } = customSelectedTimes[key];
    const updatedShifts = shifts.map((shift) => {
      const nextDay = add(parseISO(startOn), { days: 1 });
      const endOn =
        startTime >= endTime
          ? format(nextDay, DATE_FORMAT)
          : format(parseISO(startOn), DATE_FORMAT);
      return {
        ...shift,
        startTime,
        endTime,
        presetType,
        endOn,
      };
    });
    setShifts(updatedShifts);
    await bookingValidate({ shifts: updatedShifts });

    setCustomSelectedTimes((prev) => omit(prev, [key]));
    setErrors({});
  };

  useEffect(() => {
    bookingValidate({ shifts });
  }, []);

  useEffect(() => {
    if (connectedFacility) {
      bookingValidate({ shifts });
    }
  }, [connectedFacility]);

  useEffect(() => {
    return () => setSelectedDates(selectedDatesWithShifts);
  }, [selectedDatesWithShifts, setSelectedDates]);

  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)} className="hoc-shift-request-form">
      <Modal.Header closeButton>
        <Stack direction="vertical" gap={0}>
          <Modal.Title className="mx-0">
            {isBooking ? 'Schedule Shifts' : 'Select Availability'}
            {provider && (
              <span>
                : <span className="text-color-primary">{provider.display_name}</span>
              </span>
            )}
          </Modal.Title>
          {connectedFacility && (
            <div className="small text-muted">{connectedFacility.facility.name}</div>
          )}
        </Stack>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ height: '75vh' }}>
        {presets.length > 1 && (
          <div className="p-3">
            <h5 className="mb-3">
              Apply predefined time to all shifts or customize each shift separately
            </h5>
            {shifts.length && (
              <Row className="preset-list">
                {presets.map((preset, index) => (
                  <ShiftPreset
                    key={index}
                    preset={preset}
                    index={index}
                    colSize={colSize}
                    onApplyPreset={applyPreset}
                  />
                ))}
              </Row>
            )}
          </div>
        )}
        <Stack className="schedules-list p-3">
          <div className="d-flex justify-content-between">
            <h5>Shifts</h5>
            <Button variant="tertiary" size="sm" onClick={addNewShift} className="add-new-shift">
              + Add shift
            </Button>
            <AddShiftModal
              onClose={() => setOpenAddShiftModal(false)}
              show={openAddShiftModal}
              schedules={schedulesForAdding}
              onAddFields={onAddShift}
            />
          </div>
          {isValidateLoading && !shifts.length ? (
            <Row>
              <Col xs={12} className="d-flex align-items-center justify-content-center">
                <Spinner
                  aria-label="loading indicator"
                  animation="grow"
                  className="loading-spinner"
                  variant="primary"
                />
              </Col>
            </Row>
          ) : (
            shifts.map((shift) => {
              return (
                <div
                  className={classNames(
                    'schedules-list-item d-flex align-items-start custom-pb-pt-12 border-bottom',
                    { ['new-added']: shift.isNew }
                  )}
                  key={shift.id}
                >
                  <div>{SHIFTS_PRESET_MAP[shift.presetType]?.icon}</div>

                  <div className="w-100 d-flex flex-column gap-custom-12">
                    <div className="pe-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center shift-date flex-wrap">
                          <span>{parseISO(shift.startOn).toDateString()}</span>
                          {!isSameDay(new Date(shift.startOn), new Date(shift.endOn)) && (
                            <>
                              <span className="ms-1 me-1">-</span>
                              <span>{parseISO(shift.endOn).toDateString()}</span>
                            </>
                          )}
                        </div>
                        <div className="shift-rate">{formatCurrency(shift.totalRatesValue)}</div>
                      </div>
                      <Stack className="d-flex justify-content-between w-100 shift-provider-availability">
                        Provider availability: {formatProviderAvailabilityLabel(shift)}
                      </Stack>
                    </div>
                    <div className="d-flex justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <Stack
                          className="justify-content-start align-items-center wrapped"
                          direction="horizontal"
                          gap={2}
                        >
                          <Form.Group className="position-relative max-width-180">
                            <Form.Control
                              value={shift.startTime}
                              type="time"
                              className="time-input"
                              disabled={isLoading}
                              required
                              isInvalid={!!errors?.[shift.key]}
                              onChange={handleCustomTimeChange(shift, 'startTime')}
                            />
                            {errors?.[shift.key] && (
                              <Form.Control.Feedback tooltip type="invalid">
                                {errors[shift.key]}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          <Form.Group className="position-relative max-width-180">
                            <Form.Control
                              value={shift.endTime}
                              type="time"
                              className="time-input"
                              disabled={isLoading}
                              required
                              isInvalid={!!errors?.[shift.key]}
                              onChange={handleCustomTimeChange(shift, 'endTime')}
                            />
                            {errors?.[shift.key] && (
                              <Form.Control.Feedback tooltip type="invalid">
                                {errors[shift.key]}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                          <div className="d-flex flex-nowrap">
                            <IndividualShiftPresets
                              currentType={shift.presetType}
                              shiftField={shift}
                              presets={presets}
                              colSize={colSize}
                              onApplyPreset={applyIndividualPreset}
                            />
                            {!!customSelectedTimes[shift.key] && shifts.length > 1 && (
                              <Button
                                onClick={() => handleApplyToAll(shift.key)}
                                variant="link"
                                className="apply-all-btn"
                              >
                                Apply to All
                              </Button>
                            )}
                          </div>
                        </Stack>
                      </div>
                      {shifts.length > 1 && (
                        <div className="d-flex justify-content-end" style={{ minWidth: 48 }}>
                          <Button
                            variant="link"
                            className="text-danger"
                            size="lg"
                            onClick={() => handleRemoveShift(shift)}
                            title="delete"
                          >
                            <BsTrash />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </Stack>
        {isBooking && (
          <Form.Group className="p-3">
            <Form.Control
              type="text"
              as="textarea"
              placeholder="Enter comment"
              disabled={isLoading}
              isInvalid={!!formErrors?.comment}
              {...register('comment')}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex justify-content-between width-100">
          <div className="d-flex justify-content-between">
            <span>Total payment:</span>
            <span className="shifts-total-payment">{formatCurrency(totalPayment)}</span>
          </div>
          <p className="desktop hidden mb-0">
            {shifts.length} Shifts / {selectedDatesWithShifts.length} Dates
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 coll-reverse">
          <p className="mobile hidden mb-0">
            {shifts.length} Shifts / {selectedDatesWithShifts.length} Dates
          </p>
          <Button
            className="btn"
            variant="outline-primary"
            disabled={isLoading}
            onClick={onOpenConfirmPopup}
          >
            Cancel
          </Button>
          <Button
            className="btn"
            variant="primary"
            type="submit"
            disabled={
              isLoading || isValidateLoading || !isSubmitEnabled || !!Object.values(errors).length
            }
          >
            {isBooking ? 'Send Request' : 'Adjust Availability'}
          </Button>
        </div>
      </Modal.Footer>
    </Form>
  );
};
